import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DsButtonComponent } from 'src/app/shared/components/ds-button/ds-button.component';
import { ITenant } from 'src/app/modules/administration/tenant/tenant';
import { IBusinessUnit } from 'src/app/modules/master-data/business-unit/business-unit';
import { FacilityType, IFacility } from 'src/app/modules/master-data/facility/facility';
import { WorkingContextSelection } from 'src/app/core/services/context-selector.service';
import {
  IContextCatalog,
  WorkingContextCatalogService,
} from 'src/app/core/services/working-context-catalog.service';

/** Nhãn i18n cho loại cơ sở — dòng phụ của mỗi cơ sở. Mã lạ thì để trống, không hiện khoá thô. */
const FACILITY_TYPE_LABEL_KEYS: Record<number, string> = {
  [FacilityType.Warehouse]: 'Facility.Type.Warehouse',
  [FacilityType.Store]: 'Facility.Type.Store',
  [FacilityType.Office]: 'Facility.Type.Office',
  [FacilityType.Plant]: 'Facility.Type.Plant',
};

/** Loại đơn vị (BusinessUnitType 1..7 ở backend) — số lạ thì không hiện gì. */
const BUSINESS_UNIT_TYPES = [1, 2, 3, 4, 5, 6, 7];

/** Trạng thái kỳ (FiscalPeriodStatus): 1 mở · 2 khoá mềm · 3 khoá cứng. */
const FISCAL_STATUS_KEYS: Record<number, string> = {
  1: 'Layout.ContextPanel.PeriodOpen',
  2: 'Layout.ContextPanel.PeriodSoftClosed',
  3: 'Layout.ContextPanel.PeriodHardClosed',
};

/**
 * Icon nét của thiết kế, cùng sprite với module rail. Viết đủ tiền tố để cổng
 * `icons:check:sprites` đối chiếu từng tên với sprite.
 */
const PANEL_ICONS = {
  chevron: 'rail:chevron-right',
  check: 'rail:check',
  checkBold: 'rail:check-bold',
  search: 'rail:search',
  close: 'rail:close',
} as const;

const spriteHref = (name: string) => `assets/icons/rail.svg#${name.slice('rail:'.length)}`;

/** Cột Tenant chỉ hiện ô tìm khi danh sách dài hơn ngưỡng này (module-rail.jsx: > 5). */
const TENANT_SEARCH_THRESHOLD = 5;

export type BuCheckState = 'on' | 'mixed' | 'off';

/*
 * Kiểu hiển thị của panel — chung cho HAI nguồn: danh mục `org-context/get-context-catalog` và
 * ba danh sách cũ (tenant / đơn vị / cơ sở) khi danh mục chưa có (FE lên trước BE).
 */
export interface CtxTenant {
  id: string;
  code: string;
  name: string;
  displayName?: string | null;
  currencyCode?: string | null;
  plan?: string | null;
}

export interface CtxBusinessUnit {
  id: string;
  code: string;
  name: string;
  displayName?: string | null;
  tenantId: string | null;
  parentBusinessUnitId?: string | null;
  currencyCode?: string | null;
  countryCode?: string | null;
  businessUnitTypeEnum?: number | null;
}

export interface CtxFacility {
  id: string;
  code: string;
  name: string;
  displayName?: string | null;
  businessUnitId: string;
  facilityTypeEnum: number;
}

/** Một dòng của cây đơn vị kinh doanh đã trải phẳng theo trạng thái mở/đóng + tìm kiếm. */
export interface BuTreeRow {
  bu: CtxBusinessUnit;
  id: string;
  depth: number;
  hasChildren: boolean;
  open: boolean;
  state: BuCheckState;
  /** Khớp trực tiếp ô tìm (in đậm); tổ tiên chỉ hiện để giữ ngữ cảnh cây. */
  match: boolean;
  descendantCount: number;
  facilityCount: number;
  /** Tag "quốc gia · tiền tệ" — rỗng khi dữ liệu không có. */
  tag: string;
}

export interface FacilityGroup {
  bu: CtxBusinessUnit;
  /** "… › tên" khi đơn vị nằm sâu hơn một tầng — tiêu đề nhóm ngắn mà vẫn biết chỗ. */
  label: string;
  fullPath: string;
  items: CtxFacility[];
  selectedCount: number;
}

export interface FiscalSummary {
  label: string;
  /** Liệt kê kỳ theo đơn vị khi các kỳ khác nhau ("Nhiều kỳ"); rỗng khi đồng nhất. */
  tooltip: string;
}

/** Sự kiện Áp dụng: giá trị URL + header + lựa chọn đầy đủ. */
export interface WorkingContextApplyEvent extends WorkingContextSelection {
  /** Lựa chọn bị thu hẹp về một đơn vị / một cơ sở vì máy chủ chưa nhận danh sách. */
  narrowed: boolean;
}

interface ContextValues {
  businessUnitId: string | null;
  facilityId: string | null;
  headerBusinessUnitIds: string[] | null;
  headerFacilityIds: string[] | null;
  narrowed: boolean;
}

interface ParentLink {
  id: string;
  parentBusinessUnitId?: string | null;
}

interface FacilityLink {
  id: string;
  businessUnitId: string;
}

/** Các GỐC của lựa chọn: đơn vị đã chọn mà cha của nó không được chọn. */
function selectionRoots(businessUnitIds: string[], businessUnits: ParentLink[]): string[] {
  const chosen = new Set(businessUnitIds);
  const parentOf = new Map(businessUnits.map((bu) => [bu.id, bu.parentBusinessUnitId ?? null]));
  return businessUnitIds.filter((id) => {
    const parent = parentOf.get(id);
    return !(parent && chosen.has(parent));
  });
}

/**
 * Hợp đồng header CHỌN NHIỀU (lane G, `X-BusinessUnit-Context` / `X-Facility-Context` nhận danh
 * sách Guid ngăn dấu phẩy; máy chủ tự mở rộng cây con của mỗi đơn vị).
 *
 *  - Đơn vị: gửi các GỐC của lựa chọn (cha đã chọn đủ ⇒ chỉ gửi cha) — 200 Guid ≈ 7,4 KB là sát
 *    trần header 8 KB của nginx. Một gốc ⇒ đi qua URL như cũ (25 màn đọc `businessUnitId` như một
 *    Guid); nhiều gốc ⇒ URL vắng, danh sách đi qua header.
 *  - Cơ sở: một cơ sở thuộc đúng đơn vị trên URL (hoặc chưa chọn đơn vị) ⇒ đi qua URL; còn lại
 *    ⇒ danh sách qua header. Layout chỉ giữ cơ sở thuộc ĐÚNG đơn vị trên URL, nên cơ sở của một
 *    đơn vị con không được đi qua URL — nó sẽ bị bỏ im lặng.
 */
export function toContextHeaders(
  businessUnitIds: string[],
  facilityIds: string[],
  businessUnits: ParentLink[],
  facilities: FacilityLink[],
): ContextValues {
  const roots = selectionRoots(businessUnitIds, businessUnits);
  let businessUnitId = roots.length === 1 ? roots[0] : null;
  const headerBusinessUnitIds = roots.length > 1 ? roots : null;
  let facilityId: string | null = null;
  let headerFacilityIds: string[] | null = null;
  if (facilityIds.length === 1 && !headerBusinessUnitIds) {
    const facility = facilities.find((f) => f.id === facilityIds[0]);
    if (facility && (!businessUnitId || businessUnitId === facility.businessUnitId)) {
      businessUnitId = facility.businessUnitId;
      facilityId = facility.id;
    } else {
      headerFacilityIds = [...facilityIds];
    }
  } else if (facilityIds.length > 0) {
    headerFacilityIds = [...facilityIds];
  }
  return { businessUnitId, facilityId, headerBusinessUnitIds, headerFacilityIds, narrowed: false };
}

/**
 * Đường LUI khi danh mục không nạp được (404 lúc FE lên trước BE): máy chủ cũ đọc đúng MỘT Guid
 * mỗi header và coi danh sách là Guid hỏng (bỏ cả header ⇒ lặng lẽ hợp nhất toàn tenant). Nên
 * quy về cặp đơn: một cơ sở ⇒ cơ sở + đơn vị chủ; một gốc ⇒ gốc đó; còn lại ⇒ hợp nhất. Chân
 * panel báo khi lựa chọn bị thu hẹp.
 */
export function toLegacyContext(
  businessUnitIds: string[],
  facilityIds: string[],
  businessUnits: ParentLink[],
  facilities: FacilityLink[],
): ContextValues {
  const roots = selectionRoots(businessUnitIds, businessUnits);
  const facilityId = facilityIds.length === 1 ? facilityIds[0] : null;
  const facility = facilityId ? facilities.find((f) => f.id === facilityId) : undefined;
  const businessUnitId = facility ? facility.businessUnitId : roots.length === 1 ? roots[0] : null;
  return {
    businessUnitId,
    facilityId,
    headerBusinessUnitIds: null,
    headerFacilityIds: null,
    narrowed: businessUnitIds.length > 1 || facilityIds.length > 1,
  };
}

const fromTenant = (t: ITenant): CtxTenant => ({
  id: t.id!,
  code: t.code,
  name: t.name,
  displayName: t.displayName,
  currencyCode: t.currencyCode,
  // Gói chỉ có ở danh mục bối cảnh (giấy phép của tenant hiện hành).
  plan: null,
});

const fromBusinessUnit = (bu: IBusinessUnit): CtxBusinessUnit => ({
  id: bu.id!,
  code: bu.code,
  name: bu.name,
  displayName: bu.displayName,
  tenantId: bu.tenantId,
  parentBusinessUnitId: bu.parentBusinessUnitId,
  currencyCode: bu.currency?.code ?? null,
  // Quốc gia chỉ có ở danh mục bối cảnh (suy từ địa chỉ đơn vị).
  countryCode: null,
  businessUnitTypeEnum: bu.businessUnitTypeEnum ?? null,
});

const fromFacility = (f: IFacility): CtxFacility => ({
  id: f.id!,
  code: f.code,
  name: f.name,
  displayName: f.displayName,
  businessUnitId: f.businessUnitId,
  facilityTypeEnum: f.facilityTypeEnum,
});

/**
 * Panel "Bối cảnh làm việc" — module-rail.jsx :: TenantSwitcher (Carot ERP v2).
 *
 * Hộp nổi neo cạnh rail, ba cột Tenant · Đơn vị kinh doanh (cây, chọn nhiều, ba trạng thái) ·
 * Cơ sở / Kho (nhóm theo đơn vị, chọn nhiều). Mọi thay đổi nằm trong BẢN NHÁP; chỉ Áp dụng mới
 * phát `applyContext`, Huỷ / ESC / bấm lớp phủ bỏ bản nháp.
 *
 * Dữ liệu: danh mục `org-context/get-context-catalog` (đã lọc phạm vi ở máy chủ — gói, loại đơn
 * vị, quốc gia, tiền tệ báo cáo, kỳ kế toán). Trong lúc nạp, hoặc khi nạp hỏng, panel dùng ba
 * danh sách cũ do layout truyền vào (lọc quyền theo claim `allowed_*` như trước). Không chọn
 * đơn vị nào = hợp nhất toàn tenant.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'context-selector-panel',
  imports: [FormsModule, TranslateModule, DsButtonComponent],
  templateUrl: './context-selector-panel.component.html',
  styleUrls: ['./context-selector-panel.component.scss'],
})
export class ContextSelectorPanelComponent implements OnChanges, OnInit {
  private _translateService = inject(TranslateService);
  private _catalogService = inject(WorkingContextCatalogService);
  private _cdr = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);

  @Input() tenants: ITenant[] = [];
  @Input() businessUnits: IBusinessUnit[] = [];
  @Input() facilities: IFacility[] = [];
  @Input() selectedTenant: ITenant | null | undefined = null;
  @Input() selectedBusinessUnit: IBusinessUnit | null | undefined = null;
  @Input() selectedFacility: IFacility | null | undefined = null;
  /** Lựa chọn đầy đủ của lần Áp dụng trước (nhiều đơn vị / cơ sở) — chỉ dùng khi còn khớp. */
  @Input() rememberedSelection: WorkingContextSelection | null = null;

  // G3: Org claim filtering inputs
  @Input() allowedBusinessUnitIds: string[] = [];
  @Input() allowedFacilityIds: string[] = [];
  @Input() isAdmin: boolean = false;

  @Output() applyContext = new EventEmitter<WorkingContextApplyEvent>();
  @Output() closePanel = new EventEmitter<void>();

  readonly icons = {
    chevron: spriteHref(PANEL_ICONS.chevron),
    check: spriteHref(PANEL_ICONS.check),
    checkBold: spriteHref(PANEL_ICONS.checkBold),
    search: spriteHref(PANEL_ICONS.search),
    close: spriteHref(PANEL_ICONS.close),
  };

  /** 'loading' → 'ready' (danh mục có, header nhận danh sách) hoặc 'fallback' (dùng hợp đồng đơn). */
  catalogState: 'loading' | 'ready' | 'fallback' = 'loading';
  catalog: IContextCatalog | null = null;

  pendingTenantId: string | null = null;
  /** Đơn vị đã chọn trong bản nháp. Rỗng = hợp nhất toàn tenant. */
  pendingBuIds = new Set<string>();
  /** Cơ sở đã chọn trong bản nháp. Rỗng = mọi cơ sở trong phạm vi. */
  pendingFacilityIds = new Set<string>();
  expandedBuIds = new Set<string>();

  tenantSearchTerm = '';
  buSearchTerm = '';
  facilitySearchTerm = '';

  private _legacyTenants: CtxTenant[] = [];
  private _legacyBus: CtxBusinessUnit[] = [];
  private _legacyFacilities: CtxFacility[] = [];
  private _catalogTenants: CtxTenant[] = [];
  private _catalogBus: CtxBusinessUnit[] = [];
  private _catalogFacilities: CtxFacility[] = [];
  /** Người dùng đã đổi bản nháp — danh mục về muộn không được dựng lại bản nháp đè lên. */
  private _dirty = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenants'])
      this._legacyTenants = this.tenants.filter((t) => !!t.id).map(fromTenant);
    if (changes['businessUnits']) {
      this._legacyBus = this.businessUnits.filter((bu) => !!bu.id).map(fromBusinessUnit);
    }
    if (changes['facilities']) {
      this._legacyFacilities = this.facilities.filter((f) => !!f.id).map(fromFacility);
    }
    if (
      changes['selectedTenant'] ||
      changes['selectedBusinessUnit'] ||
      changes['selectedFacility'] ||
      changes['rememberedSelection']
    ) {
      this._resetDraft();
    }
  }

  ngOnInit(): void {
    this._catalogService
      .load()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((catalog) => {
        this._applyCatalog(catalog);
        this._cdr.markForCheck();
      });
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closePanel.emit();
  }

  private _applyCatalog(catalog: IContextCatalog | null): void {
    this.catalog = catalog;
    this.catalogState = catalog ? 'ready' : 'fallback';
    if (!catalog) return;
    this._catalogTenants = catalog.tenants.map((t) => ({
      id: t.id,
      code: t.code,
      name: t.name,
      displayName: t.displayName,
      currencyCode: t.currencyCode,
      plan: t.plan,
    }));
    this._catalogBus = catalog.businessUnits.map((bu) => ({
      id: bu.id,
      code: bu.code,
      name: bu.name,
      displayName: bu.displayName,
      tenantId: catalog.currentTenantId,
      parentBusinessUnitId: bu.parentBusinessUnitId,
      currencyCode: bu.currencyCode,
      countryCode: bu.countryCode,
      businessUnitTypeEnum: bu.businessUnitTypeEnum,
    }));
    this._catalogFacilities = catalog.facilities.map((f) => ({
      id: f.id,
      code: f.code,
      name: f.name,
      displayName: f.displayName,
      businessUnitId: f.businessUnitId,
      facilityTypeEnum: f.facilityTypeEnum,
    }));
    if (!this._dirty) this._resetDraft();
  }

  /** Danh mục áp dụng cho tenant đang chọn trong bản nháp (danh mục chỉ mô tả tenant hiện hành). */
  private get _catalogForPending(): boolean {
    return !!this.catalog && this.pendingTenantId === this.catalog.currentTenantId;
  }

  // ── Tenant ────────────────────────────────────────────────────────────────

  get tenantList(): CtxTenant[] {
    if (!this.catalog || this._catalogTenants.length === 0) return this._legacyTenants;
    // Gói chỉ đọc được của tenant hiện hành; tenant chỉ có trong danh sách cũ vẫn phải hiện.
    const byId = new Map(this._catalogTenants.map((t) => [t.id, t]));
    const merged = this._legacyTenants.map((t) => byId.get(t.id) ?? t);
    for (const t of this._catalogTenants) {
      if (!merged.some((m) => m.id === t.id)) merged.push(t);
    }
    return merged;
  }

  get showTenantSearch(): boolean {
    return this.tenantList.length > TENANT_SEARCH_THRESHOLD;
  }

  get filteredTenants(): CtxTenant[] {
    const term = this.tenantSearchTerm.trim().toLowerCase();
    if (!term) return this.tenantList;
    return this.tenantList.filter((t) =>
      `${t.displayName || t.name || ''} ${t.code || ''}`.toLowerCase().includes(term),
    );
  }

  get pendingTenant(): CtxTenant | undefined {
    return this.tenantList.find((t) => t.id === this.pendingTenantId);
  }

  tenantName(t: CtxTenant | undefined): string {
    return t ? t.displayName || t.name || '' : '';
  }

  /** Chữ cái đầu của các từ viết hoa (tối đa 2) — ô 30px của mỗi tenant. */
  tenantInitials(t: CtxTenant): string {
    const name = this.tenantName(t).trim();
    const caps = name
      .split(/\s+/)
      .filter((w) => /^\p{Lu}/u.test(w))
      .map((w) => w[0]);
    return (caps.length ? caps.slice(0, 2).join('') : name.slice(0, 2)).toUpperCase();
  }

  onTenantSelect(tenantId: string): void {
    if (tenantId === this.pendingTenantId) return;
    this._dirty = true;
    this.pendingTenantId = tenantId;
    this.pendingBuIds = new Set();
    this.pendingFacilityIds = new Set();
    this.buSearchTerm = '';
    this.facilitySearchTerm = '';
    this.expandedBuIds = this._defaultExpanded();
  }

  /** Mở sẵn gốc; một gốc duy nhất thì mở thêm tầng hai có con (thiết kế: GRP + VN-HQ). */
  private _defaultExpanded(): Set<string> {
    const kids = this._childrenMap();
    const roots = kids.get('') ?? [];
    const expanded = new Set(roots.map((bu) => bu.id));
    if (roots.length === 1) {
      for (const child of kids.get(roots[0].id) ?? []) {
        if (kids.has(child.id)) expanded.add(child.id);
      }
    }
    return expanded;
  }

  // ── Đơn vị kinh doanh (cây) ───────────────────────────────────────────────

  /** Đơn vị của tenant trong bản nháp, đã lọc theo quyền phạm vi. */
  get businessUnitsForTenant(): CtxBusinessUnit[] {
    const source = this._catalogForPending ? this._catalogBus : this._legacyBus;
    let filtered = source.filter((bu) => bu.tenantId === this.pendingTenantId);
    // G3: Non-admin users with org claims see only their allowed BUs
    if (!this.isAdmin && this.allowedBusinessUnitIds.length > 0 && !this._catalogForPending) {
      filtered = filtered.filter((bu) => this.allowedBusinessUnitIds.includes(bu.id));
    }
    return filtered;
  }

  /**
   * Con theo cha, chỉ trong tập đơn vị nhìn thấy. Đơn vị có cha nằm NGOÀI tập (bị lọc quyền,
   * hoặc khác tenant) được coi là gốc — không mất khỏi cây.
   */
  private _childrenMap(): Map<string, CtxBusinessUnit[]> {
    const visible = this.businessUnitsForTenant;
    const ids = new Set(visible.map((bu) => bu.id));
    const map = new Map<string, CtxBusinessUnit[]>();
    for (const bu of visible) {
      const parent =
        bu.parentBusinessUnitId && ids.has(bu.parentBusinessUnitId) ? bu.parentBusinessUnitId : '';
      const list = map.get(parent) ?? [];
      list.push(bu);
      map.set(parent, list);
    }
    return map;
  }

  /** Chính đơn vị + mọi đơn vị con cháu (trong tập nhìn thấy). */
  descendantIds(id: string, kids = this._childrenMap()): string[] {
    const out = [id];
    for (const child of kids.get(id) ?? []) out.push(...this.descendantIds(child.id, kids));
    return out;
  }

  nodeState(id: string, kids = this._childrenMap()): BuCheckState {
    const all = this.descendantIds(id, kids);
    const n = all.filter((x) => this.pendingBuIds.has(x)).length;
    return n === all.length ? 'on' : n > 0 ? 'mixed' : 'off';
  }

  /** Nhãn loại đơn vị đã dịch; rỗng khi không có dữ liệu hoặc số lạ. */
  businessUnitTypeLabel(bu: CtxBusinessUnit): string {
    const n = bu.businessUnitTypeEnum;
    return n && BUSINESS_UNIT_TYPES.includes(n)
      ? this._translateService.instant(`Layout.ContextPanel.BusinessUnitType.${n}`)
      : '';
  }

  private _buMatches(bu: CtxBusinessUnit, term: string): boolean {
    return [
      bu.displayName || bu.name,
      bu.code,
      this.businessUnitTypeLabel(bu),
      bu.countryCode,
      bu.currencyCode,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term);
  }

  get buRows(): BuTreeRow[] {
    const kids = this._childrenMap();
    const visible = this.businessUnitsForTenant;
    const term = this.buSearchTerm.trim().toLowerCase();
    let keep: Set<string> | null = null;
    if (term) {
      // Khớp + mọi tổ tiên, để kết quả vẫn nằm đúng chỗ trong cây.
      keep = new Set<string>();
      const byId = new Map(visible.map((bu) => [bu.id, bu]));
      for (const bu of visible) {
        if (!this._buMatches(bu, term)) continue;
        let cur: CtxBusinessUnit | undefined = bu;
        while (cur && !keep.has(cur.id)) {
          keep.add(cur.id);
          cur = cur.parentBusinessUnitId ? byId.get(cur.parentBusinessUnitId) : undefined;
        }
      }
    }
    const tenantCcy = this.pendingTenant?.currencyCode ?? null;
    const facCount = new Map<string, number>();
    for (const f of this.facilitiesForTenant) {
      facCount.set(f.businessUnitId, (facCount.get(f.businessUnitId) ?? 0) + 1);
    }
    const rows: BuTreeRow[] = [];
    const walk = (list: CtxBusinessUnit[], depth: number) => {
      for (const bu of list) {
        const id = bu.id;
        if (keep && !keep.has(id)) continue;
        const children = kids.get(id) ?? [];
        const open = keep ? true : this.expandedBuIds.has(id);
        const ccy = bu.currencyCode ?? null;
        // Thiết kế: tag ở gốc, và ở mọi đơn vị khác quốc gia / tiền tệ với tenant.
        const foreign = !!ccy && ccy !== tenantCcy;
        const showTag = depth === 0 || foreign;
        rows.push({
          bu,
          id,
          depth,
          hasChildren: children.length > 0,
          open,
          state: this.nodeState(id, kids),
          match: !!term && this._buMatches(bu, term),
          descendantCount: this.descendantIds(id, kids).length - 1,
          facilityCount: facCount.get(id) ?? 0,
          tag: showTag ? [bu.countryCode, ccy].filter(Boolean).join(' · ') : '',
        });
        if (open) walk(children, depth + 1);
      }
    };
    walk(kids.get('') ?? [], 0);
    return rows;
  }

  /** Phần sau mã của dòng phụ: " · loại · N đơn vị con · N cơ sở" (mã in mono riêng). */
  buSubLine(row: BuTreeRow): string {
    const parts: string[] = [];
    const type = this.businessUnitTypeLabel(row.bu);
    if (type) parts.push(type);
    if (row.descendantCount > 0) {
      parts.push(
        this._translateService.instant('Layout.ContextPanel.ChildUnitCount', {
          count: row.descendantCount,
        }),
      );
    }
    if (row.facilityCount > 0) {
      parts.push(
        this._translateService.instant('Layout.ContextPanel.FacilityCount', {
          count: row.facilityCount,
        }),
      );
    }
    return parts.length ? ` · ${parts.join(' · ')}` : '';
  }

  /** Chọn cha = chọn cả cây con; bỏ khi cả cây đã chọn. Cơ sở ngoài phạm vi mới bị gỡ. */
  toggleBu(id: string): void {
    this._dirty = true;
    const kids = this._childrenMap();
    const sub = this.descendantIds(id, kids);
    const full = sub.every((x) => this.pendingBuIds.has(x));
    const next = new Set(this.pendingBuIds);
    for (const x of sub) {
      if (full) next.delete(x);
      else next.add(x);
    }
    this.pendingBuIds = next;
    const inScope = new Set(this.facilitiesForScope.map((f) => f.id));
    this.pendingFacilityIds = new Set([...this.pendingFacilityIds].filter((f) => inScope.has(f)));
  }

  toggleExpand(row: BuTreeRow): void {
    if (!row.hasChildren || this.buSearchTerm.trim()) return;
    const next = new Set(this.expandedBuIds);
    if (next.has(row.id)) next.delete(row.id);
    else next.add(row.id);
    this.expandedBuIds = next;
  }

  expandAll(): void {
    this.expandedBuIds = new Set(this.businessUnitsForTenant.map((bu) => bu.id));
  }

  collapseAll(): void {
    this.expandedBuIds = new Set();
  }

  get buCountLabel(): string {
    const total = this.businessUnitsForTenant.length;
    return this.pendingBuIds.size ? `${this.pendingBuIds.size}/${total}` : `${total}`;
  }

  // ── Cơ sở / Kho ───────────────────────────────────────────────────────────

  /** Cơ sở của mọi đơn vị nhìn thấy trong tenant, đã lọc quyền — để đếm trên cây. */
  get facilitiesForTenant(): CtxFacility[] {
    const source = this._catalogForPending ? this._catalogFacilities : this._legacyFacilities;
    const buIds = new Set(this.businessUnitsForTenant.map((bu) => bu.id));
    let filtered = source.filter((f) => buIds.has(f.businessUnitId));
    // G3: Non-admin users with org claims see only their allowed facilities
    if (!this.isAdmin && this.allowedFacilityIds.length > 0 && !this._catalogForPending) {
      filtered = filtered.filter((f) => this.allowedFacilityIds.includes(f.id));
    }
    return filtered;
  }

  /** Đơn vị trong phạm vi: các đơn vị đã chọn, hoặc tất cả khi chưa chọn đơn vị nào. */
  get scopeBusinessUnits(): CtxBusinessUnit[] {
    const visible = this.businessUnitsForTenant;
    return this.pendingBuIds.size ? visible.filter((bu) => this.pendingBuIds.has(bu.id)) : visible;
  }

  get facilitiesForScope(): CtxFacility[] {
    const scope = new Set(this.scopeBusinessUnits.map((bu) => bu.id));
    return this.facilitiesForTenant.filter((f) => scope.has(f.businessUnitId));
  }

  facilityTypeLabel(facility: CtxFacility): string {
    const key = FACILITY_TYPE_LABEL_KEYS[facility.facilityTypeEnum];
    return key ? this._translateService.instant(key) : '';
  }

  get filteredFacilities(): CtxFacility[] {
    const term = this.facilitySearchTerm.trim().toLowerCase();
    if (!term) return this.facilitiesForScope;
    return this.facilitiesForScope.filter((f) =>
      `${f.displayName || f.name || ''} ${f.code || ''} ${this.facilityTypeLabel(f)}`
        .toLowerCase()
        .includes(term),
    );
  }

  get facilityGroups(): FacilityGroup[] {
    const list = this.filteredFacilities;
    const byId = new Map(this.businessUnitsForTenant.map((bu) => [bu.id, bu]));
    const groups: FacilityGroup[] = [];
    for (const bu of this.scopeBusinessUnits) {
      const items = list.filter((f) => f.businessUnitId === bu.id);
      if (!items.length) continue;
      const path: string[] = [];
      let cur = bu.parentBusinessUnitId ? byId.get(bu.parentBusinessUnitId) : undefined;
      while (cur) {
        path.unshift(cur.displayName || cur.name);
        cur = cur.parentBusinessUnitId ? byId.get(cur.parentBusinessUnitId) : undefined;
      }
      const name = bu.displayName || bu.name;
      groups.push({
        bu,
        label: path.length > 1 ? `… › ${name}` : name,
        fullPath: [...path, name].join(' › '),
        items,
        selectedCount: items.filter((f) => this.pendingFacilityIds.has(f.id)).length,
      });
    }
    return groups;
  }

  isFacilityOn(f: CtxFacility): boolean {
    return this.pendingFacilityIds.has(f.id);
  }

  toggleFacility(f: CtxFacility): void {
    this._dirty = true;
    const next = new Set(this.pendingFacilityIds);
    if (next.has(f.id)) next.delete(f.id);
    else next.add(f.id);
    this.pendingFacilityIds = next;
  }

  get allVisibleFacilitiesState(): BuCheckState {
    const list = this.filteredFacilities;
    const n = list.filter((f) => this.pendingFacilityIds.has(f.id)).length;
    return list.length > 0 && n === list.length ? 'on' : n > 0 ? 'mixed' : 'off';
  }

  toggleAllVisibleFacilities(): void {
    this._dirty = true;
    const list = this.filteredFacilities.map((f) => f.id);
    const next = new Set(this.pendingFacilityIds);
    if (this.allVisibleFacilitiesState === 'on') list.forEach((id) => next.delete(id));
    else list.forEach((id) => next.add(id));
    this.pendingFacilityIds = next;
  }

  clearFacilities(): void {
    this._dirty = true;
    this.pendingFacilityIds = new Set();
  }

  // ── Dải tóm tắt ───────────────────────────────────────────────────────────

  get buSummary(): string {
    const total = this.businessUnitsForTenant.length;
    const chosen = this.pendingBuIds;
    if (chosen.size === 0 || chosen.size === total) {
      return this._translateService.instant('Layout.ContextPanel.AllUnitsCount', { count: total });
    }
    const tops = selectionRoots([...chosen], this.businessUnitsForTenant);
    if (tops.length === 1) {
      const bu = this.businessUnitsForTenant.find((x) => x.id === tops[0]);
      const name = bu?.displayName || bu?.name || '';
      return chosen.size > 1
        ? this._translateService.instant('Layout.ContextPanel.UnitPlusChildren', {
            name,
            count: chosen.size - 1,
          })
        : name;
    }
    return this._translateService.instant('Layout.ContextPanel.UnitCount', { count: chosen.size });
  }

  get facilitySummary(): string {
    const chosen = this.pendingFacilityIds;
    if (chosen.size === 0) {
      return this._translateService.instant('Layout.ContextPanel.AllFacilitiesCount', {
        count: this.facilitiesForScope.length,
      });
    }
    if (chosen.size === 1) {
      const f = this.facilitiesForTenant.find((x) => x.id === [...chosen][0]);
      return f?.displayName || f?.name || '';
    }
    return this._translateService.instant('Layout.ContextPanel.FacilityCount', {
      count: chosen.size,
    });
  }

  /**
   * Tiền tệ báo cáo: `reportingCurrency` của danh mục (rơi về tiền tệ tenant). Phạm vi của BẢN
   * NHÁP gồm đơn vị dùng tiền tệ khác ⇒ ghi rõ là số đã quy đổi. Không có mã ⇒ ẩn mục.
   */
  get reportingCurrencySummary(): string | null {
    const base =
      (this._catalogForPending ? this.catalog?.reportingCurrency?.code : null) ??
      this.pendingTenant?.currencyCode;
    if (!base) return null;
    const codes = new Set(
      this.scopeBusinessUnits.map((bu) => bu.currencyCode).filter((c): c is string => !!c),
    );
    codes.add(base);
    return codes.size > 1
      ? this._translateService.instant('Layout.ContextPanel.ConvertedFrom', {
          code: base,
          count: codes.size,
        })
      : base;
  }

  private _periodLabel(p: { periodName: string; statusEnum: number }): string {
    const key = FISCAL_STATUS_KEYS[p.statusEnum];
    return key ? `${p.periodName} · ${this._translateService.instant(key)}` : p.periodName;
  }

  /**
   * Kỳ kế toán hiện hành của phạm vi bản nháp: kỳ CHUNG của tenant (businessUnitId null) + kỳ
   * riêng của đơn vị trong phạm vi. Đồng nhất (cùng mã kỳ, cùng trạng thái mở) ⇒ hiện kỳ; khác
   * nhau ⇒ "Nhiều kỳ" + tooltip liệt kê theo đơn vị. Danh mục chỉ có kỳ của bối cảnh ĐANG áp
   * dụng — đơn vị mới thêm trong bản nháp chưa có kỳ riêng thì không xuất hiện.
   */
  get fiscalSummary(): FiscalSummary | null {
    if (!this._catalogForPending) return null;
    const scope = new Set(this.scopeBusinessUnits.map((bu) => bu.id));
    const periods = (this.catalog?.currentFiscalPeriods ?? []).filter(
      (p) => p.businessUnitId == null || scope.has(p.businessUnitId),
    );
    if (periods.length === 0) return null;
    const uniform = periods.every(
      (p) => p.periodCode === periods[0].periodCode && p.isOpen === periods[0].isOpen,
    );
    if (uniform) return { label: this._periodLabel(periods[0]), tooltip: '' };
    const name = (id: string | null) => {
      if (!id) return this._translateService.instant('Layout.ContextPanel.WholeTenantPeriod');
      const bu = this.businessUnitsForTenant.find((b) => b.id === id);
      return bu?.displayName || bu?.name || id;
    };
    return {
      label: this._translateService.instant('Layout.ContextPanel.MultiplePeriods'),
      tooltip: periods.map((p) => `${name(p.businessUnitId)}: ${this._periodLabel(p)}`).join('\n'),
    };
  }

  // ── Áp dụng / Huỷ ─────────────────────────────────────────────────────────

  /** Giá trị sẽ gửi đi: header danh sách khi danh mục có, hợp đồng đơn khi rơi về. */
  get contextPreview(): ContextValues {
    const fn = this.catalogState === 'ready' ? toContextHeaders : toLegacyContext;
    return fn(
      [...this.pendingBuIds],
      [...this.pendingFacilityIds],
      this.businessUnitsForTenant,
      this.facilitiesForTenant,
    );
  }

  /** Dòng báo ở chân — CHỈ ở đường lui (máy chủ cũ chưa nhận danh sách). */
  get narrowedNotice(): string | null {
    const values = this.contextPreview;
    if (!values.narrowed) return null;
    let name: string;
    if (values.facilityId) {
      const f = this.facilitiesForTenant.find((x) => x.id === values.facilityId);
      name = f?.displayName || f?.name || '';
    } else if (values.businessUnitId) {
      const bu = this.businessUnitsForTenant.find((x) => x.id === values.businessUnitId);
      name = bu?.displayName || bu?.name || '';
    } else {
      name = this._translateService.instant('Layout.ContextPanel.WholeTenant');
    }
    return this._translateService.instant('Layout.ContextPanel.NarrowedNotice', { name });
  }

  get canApply(): boolean {
    return !!this.pendingTenant;
  }

  onApply(): void {
    if (!this.canApply || !this.pendingTenantId) return;
    const values = this.contextPreview;
    this.applyContext.emit({
      tenantId: this.pendingTenantId,
      businessUnitIds: [...this.pendingBuIds],
      facilityIds: [...this.pendingFacilityIds],
      businessUnitId: values.businessUnitId,
      facilityId: values.facilityId,
      headerBusinessUnitIds: values.headerBusinessUnitIds,
      headerFacilityIds: values.headerFacilityIds,
      narrowed: values.narrowed,
    });
  }

  onCancel(): void {
    this._resetDraft();
    this.closePanel.emit();
  }

  onBackdropClick(): void {
    this.closePanel.emit();
  }

  /**
   * Bản nháp = bối cảnh đang áp dụng. Dùng lựa chọn đã nhớ nếu nó vẫn khớp cặp đơn đang chạy
   * (không thì người dùng đã đổi bối cảnh bằng đường khác, ví dụ URL); nếu không, dựng từ cặp
   * đơn: đơn vị đang chọn ⇒ đơn vị đó + cây con, cơ sở đang chọn ⇒ cơ sở đó.
   */
  private _resetDraft(): void {
    this._dirty = false;
    this.pendingTenantId = this.selectedTenant?.id || null;
    const buId = this.selectedBusinessUnit?.id || null;
    const facId = this.selectedFacility?.id || null;
    const mem = this.rememberedSelection;
    if (
      mem &&
      mem.tenantId === this.pendingTenantId &&
      mem.businessUnitId === buId &&
      mem.facilityId === facId
    ) {
      this.pendingBuIds = new Set(mem.businessUnitIds);
      this.pendingFacilityIds = new Set(mem.facilityIds);
    } else {
      this.pendingBuIds = new Set(buId ? this.descendantIds(buId) : []);
      this.pendingFacilityIds = new Set(facId ? [facId] : []);
    }
    // Mở sẵn mặc định + đường tới mọi đơn vị đã chọn.
    const byId = new Map(this.businessUnitsForTenant.map((bu) => [bu.id, bu]));
    const expanded = this._defaultExpanded();
    for (const id of this.pendingBuIds) {
      let parent = byId.get(id)?.parentBusinessUnitId;
      while (parent && !expanded.has(parent)) {
        expanded.add(parent);
        parent = byId.get(parent)?.parentBusinessUnitId;
      }
    }
    this.expandedBuIds = expanded;
    this.tenantSearchTerm = '';
    this.buSearchTerm = '';
    this.facilitySearchTerm = '';
  }
}
